import React from 'react';
import styled, { css } from 'styled-components';
import {
  FaWarehouse,
  FaStoreAlt,
  FaBoxes,
  FaShoppingBag,
  FaCheck,
  FaChevronLeft,
  FaCar,
  FaTruck,
} from 'react-icons/fa';
import {
  fadeInUp,
  slideInForward,
  popIn,
  checkPop,
  reducedMotion,
} from './partnerSignupAnimations';

export const SIGNUP_SELECTION_KEY = 'signup_account_selection';

export const NEW_SELLER_CATEGORIES = [
  { id: 'warehouse', label: 'Warehouse', desc: 'Bulk storage & distribution', icon: FaWarehouse },
  { id: 'shop', label: 'Shop', desc: 'Physical retail storefront', icon: FaStoreAlt },
  { id: 'wholesaler', label: 'Wholesaler', desc: 'B2B bulk supply', icon: FaBoxes },
  { id: 'retail', label: 'Retail', desc: 'Direct customer sales', icon: FaShoppingBag },
];

export const USED_SELLER_CATEGORIES = [
  { id: 'medium_scrapyard', label: 'Medium scrapyard', desc: '1–10 cars', icon: FaCar },
  { id: 'large_scrapyard', label: 'Large scrapyard', desc: '1–20 cars', icon: FaTruck },
  { id: 'shop_sparepart', label: 'Shop only spare parts', desc: 'Spare parts retail shop', icon: FaStoreAlt },
];

export function saveSignupSelection(selection) {
  localStorage.setItem(SIGNUP_SELECTION_KEY, JSON.stringify(selection));
}

export function loadSignupSelection() {
  try {
    const raw = localStorage.getItem(SIGNUP_SELECTION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearSignupSelection() {
  localStorage.removeItem(SIGNUP_SELECTION_KEY);
}

export function hasRoleSelected(selection) {
  if (!selection?.mainType) return false;
  if (selection.mainType === 'garage') return Boolean(selection.sellerCategory);
  if (selection.mainType === 'sparepart_seller') {
    if (!selection.partCondition) return false;
    if (selection.partCondition === 'used') return Boolean(selection.sellerCategory);
    return Boolean(selection.sellerCategory);
  }
  return false;
}

export function isSignupSelectionComplete(selection) {
  if (!selection?.mainType) return false;
  if (selection.mainType === 'garage') return Boolean(selection.sellerCategory);
  if (selection.mainType === 'sparepart_seller') {
    if (!selection.partCondition) return false;
    if (selection.partCondition === 'used') return Boolean(selection.sellerCategory);
    if (selection.partCondition === 'new') return Boolean(selection.sellerCategory);
  }
  return false;
}

function panelKey(selection) {
  const { partCondition, sellerCategory } = selection;
  if (!partCondition) return 'condition';
  if (partCondition === 'used') return 'used';
  return `category-${sellerCategory || 'pick'}`;
}

const AnimatedWrap = styled.div`
  animation: ${slideInForward} 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
  ${reducedMotion}
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: var(--pp-muted);
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  animation: ${fadeInUp} 0.35s ease both;
  ${reducedMotion}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns || 2}, 1fr);
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 14px 16px;
  border: 2px solid ${(p) => (p.$selected ? 'var(--pp-brand)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-card);
  background: ${(p) => (p.$selected ? 'var(--pp-brand-bg)' : 'var(--pp-surface)')};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.25s ease, background 0.25s ease, transform 0.25s ease;
  animation: ${fadeInUp} 0.4s ease ${(p) => 0.06 + (p.$delay || 0) * 0.08}s both;
  ${reducedMotion}

  &:hover {
    border-color: var(--pp-brand);
    transform: translateY(-2px);
  }

  ${(p) => p.$selected && css`
    transform: scale(1.02);
    animation: ${popIn} 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
    ${reducedMotion}
  `}
`;

const OptionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--pp-text);
`;

const OptionDesc = styled.span`
  font-size: 12px;
  color: var(--pp-muted);
  line-height: 1.45;
`;

const CheckMark = styled(FaCheck)`
  position: absolute;
  top: 12px;
  right: 12px;
  color: var(--pp-brand);
  font-size: 14px;
  animation: ${checkPop} 0.3s ease both;
  ${reducedMotion}
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin-bottom: 14px;
  border: none;
  background: none;
  color: var(--pp-brand);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    text-decoration: underline;
    transform: translateX(-3px);
  }
`;

/** Step 2 sub-options for sparepart sellers (new/used, business type). */
export default function SignUpAccountSelect({ selection, onChange }) {
  const { mainType, partCondition, sellerCategory } = selection;

  if (mainType !== 'sparepart_seller') return null;

  const handlePartCondition = (condition) => {
    onChange({
      ...selection,
      partCondition: condition,
      sellerCategory: condition === 'new' ? selection.sellerCategory : null,
    });
  };

  const handleSellerCategory = (category) => {
    onChange({ ...selection, sellerCategory: category });
  };

  const key = panelKey(selection);

  if (!partCondition) {
    return (
      <AnimatedWrap key={key}>
        <Section>
          <SectionLabel>Part condition</SectionLabel>
          <Grid $columns={2}>
            <OptionCard type="button" $selected={false} $delay={0} onClick={() => handlePartCondition('new')}>
              <OptionTitle>New parts</OptionTitle>
              <OptionDesc>Brand-new OEM & aftermarket parts</OptionDesc>
            </OptionCard>
            <OptionCard type="button" $selected={false} $delay={1} onClick={() => handlePartCondition('used')}>
              <OptionTitle>Used parts</OptionTitle>
              <OptionDesc>Pre-owned & salvaged parts</OptionDesc>
            </OptionCard>
          </Grid>
        </Section>
      </AnimatedWrap>
    );
  }

  if (partCondition === 'used') {
    return (
      <AnimatedWrap key={key}>
        <Section>
          <BackBtn type="button" onClick={() => onChange({ ...selection, partCondition: null, sellerCategory: null })}>
            <FaChevronLeft size={10} /> Back to part condition
          </BackBtn>
          <SectionLabel>Business type</SectionLabel>
          <Grid $columns={3}>
            {USED_SELLER_CATEGORIES.map(({ id, label, desc, icon: Icon }, index) => (
              <OptionCard
                key={id}
                type="button"
                $selected={sellerCategory === id}
                $delay={index}
                onClick={() => handleSellerCategory(id)}
              >
                <OptionTitle>
                  <Icon style={{ marginRight: 6, fontSize: 13, color: 'var(--pp-brand)' }} />
                  {label}
                </OptionTitle>
                <OptionDesc>{desc}</OptionDesc>
              </OptionCard>
            ))}
          </Grid>
        </Section>
      </AnimatedWrap>
    );
  }

  return (
    <AnimatedWrap key={key}>
      <Section>
        <BackBtn type="button" onClick={() => onChange({ ...selection, partCondition: null, sellerCategory: null })}>
          <FaChevronLeft size={10} /> Back to part condition
        </BackBtn>
        <SectionLabel>Business type</SectionLabel>
        <Grid $columns={2}>
          {NEW_SELLER_CATEGORIES.map(({ id, label, desc, icon: Icon }, index) => (
            <OptionCard
              key={id}
              type="button"
              $selected={sellerCategory === id}
              $delay={index}
              onClick={() => handleSellerCategory(id)}
            >
              <OptionTitle>
                <Icon style={{ marginRight: 6, fontSize: 13, color: 'var(--pp-brand)' }} />
                {label}
              </OptionTitle>
              <OptionDesc>{desc}</OptionDesc>
              {sellerCategory === id && <CheckMark />}
            </OptionCard>
          ))}
        </Grid>
      </Section>
    </AnimatedWrap>
  );
}
